import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityStatus } from 'infrastructure/enums';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { Button } from 'antd';
import { BroadcastTopics } from 'services/wsService';
import { useSocketOn } from 'infrastructure/hooks/websockets/use-socket-on';

import type { SocketOnNewReadingPayload } from 'services/wsService/types/newReading';

export const useNewReadings = ({
  type,
  patientGuid,
  hasUnsavedMessage,
  setPage,
  refetch,
  page,
  resetActions,
  onReadingsRefetch,
}: {
  type: ActivityStatus | undefined;
  patientGuid: string;
  hasUnsavedMessage: boolean;
  setPage: (page: number) => void;
  refetch: () => void;
  page: number;
  resetActions: () => void;
  onReadingsRefetch?: () => void;
}) => {
  const { t } = useTranslation();

  const handleNewReading = useCallback(
    (data: SocketOnNewReadingPayload) => {
      const isUnactioned = type === ActivityStatus.Unactioned;
      const isPatientGuidMatch = data.patientGuid === patientGuid;
      if (!isUnactioned || !isPatientGuidMatch) return;

      if (hasUnsavedMessage) {
        const alertKey = 'newReadingHasUnsavedMessage';
        notificationController.info({
          key: alertKey,
          message: t('messages.newReadingHasUnsavedMessage'),
          duration: 6,
          actions: (
            <Button
              onClick={() => {
                setPage(1);
                resetActions();
                refetch();
                notificationController.destroy(alertKey);
                onReadingsRefetch?.();
              }}
            >
              {t('controls.getLatestReadings')}
            </Button>
          ),
        });
        return;
      }

      if (page !== 1) {
        const alertKey = 'newReadingGoToFirstPage';
        notificationController.info({
          key: alertKey,
          message: t('messages.newReadingGoToFirstPage'),
          duration: 6,
          actions: (
            <Button
              onClick={() => {
                setPage(1);
                notificationController.destroy(alertKey);
                onReadingsRefetch?.();
              }}
            >
              {t('controls.getLatestReadings')}
            </Button>
          ),
        });
        return;
      }

      refetch();
      onReadingsRefetch?.();
    },
    [type, patientGuid, page, hasUnsavedMessage, refetch, onReadingsRefetch],
  );

  useSocketOn<SocketOnNewReadingPayload>(
    BroadcastTopics.NewReading,
    handleNewReading,
  );
};
